import { useEffect, useState } from "react";
import { Calendar, MapPin, Clock, ArrowRight } from "lucide-react";
import SectionEyebrow from "@/components/SectionEyebrow";
import EventRegisterModal from "@/components/EventRegisterModal";
import { IMAGES } from "@/data/content";
import api from "@/lib/api";

const CATEGORIES = ["Tous", "Prière", "Conférence", "Croisade", "Veillée", "Séminaire"];

const Events = () => {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("Tous");
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
  }, []);

  const filtered = filter === "Tous" ? events : events.filter((e) => e.category === filter);

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="events-page">
      <section className="relative min-h-[55vh] flex items-end overflow-hidden">
        <img src={IMAGES.hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B3D91]/90 via-[#0B3D91]/60 to-[#0B3D91]/30" />
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative mv-container py-24 text-white">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-white/80" />
            <span className="text-xs uppercase tracking-[0.3em] font-bold">Nos événements</span>
          </div>
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Rejoignez-nous.<br />Vivez la rencontre.
          </h1>
        </div>
      </section>

      <section className="mv-section bg-white">
        <div className="mv-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <SectionEyebrow>À venir</SectionEyebrow>
              <h2 className="mt-5 font-heading font-black text-4xl tracking-tight text-[#1C1C1C]">
                Rencontres de prière, conférences & séminaires.
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  data-testid={`filter-${c}`}
                  onClick={() => setFilter(c)}
                  className={`px-4 py-2 text-sm font-medium border transition-colors ${
                    filter === c
                      ? "bg-[#0B3D91] text-white border-[#0B3D91]"
                      : "bg-white text-[#1C1C1C] border-gray-300 hover:border-[#0B3D91]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20 text-muted">Aucun événement à afficher.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((ev) => (
                <article
                  key={ev.id}
                  className="group flex flex-col border border-gray-200 bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`event-card-${ev.id}`}
                >
                  {/* Text-only card — no image area */}
                  <div className="p-8 flex-1 flex flex-col">
                    {/* Date badge */}
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-16 w-16 bg-[#0B3D91] text-white flex flex-col items-center justify-center shrink-0">
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">
                          {new Date(ev.date).toLocaleDateString("fr-FR", { month: "short" }).replace(".", "")}
                        </div>
                        <div className="font-heading font-black text-2xl leading-none">
                          {new Date(ev.date).toLocaleDateString("fr-FR", { day: "2-digit" })}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0B3D91]">
                          {ev.category}
                        </div>
                        <div className="text-xs text-muted mt-1">
                          {new Date(ev.date).toLocaleDateString("fr-FR", { year: "numeric" })}
                        </div>
                      </div>
                    </div>

                    <h3 className="font-heading font-bold text-2xl text-[#1C1C1C] leading-tight">
                      {ev.title}
                    </h3>
                    <p className="mt-3 text-sm text-muted leading-relaxed">{ev.description}</p>

                    <div className="mt-6 space-y-2.5 text-sm">
                      <div className="flex items-center gap-2 text-[#1C1C1C]">
                        <Calendar size={16} className="text-[#0B3D91] shrink-0" />
                        <span>{formatDate(ev.date)}</span>
                      </div>
                      {ev.time && (
                        <div className="flex items-center gap-2 text-[#1C1C1C]">
                          <Clock size={16} className="text-[#0B3D91] shrink-0" />
                          <span>{ev.time}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-[#1C1C1C]">
                        <MapPin size={16} className="text-[#0B3D91] shrink-0" />
                        <span>{ev.location}</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        if (ev.register_url) {
                          window.open(ev.register_url, "_blank", "noopener,noreferrer");
                        } else {
                          setSelectedEvent(ev);
                        }
                      }}
                      className="mt-8 btn-primary !py-3 !px-5 text-sm self-start"
                      data-testid={`event-register-${ev.id}`}
                    >
                      S'inscrire <ArrowRight size={14} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {selectedEvent && (
        <EventRegisterModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </div>
  );
};

export default Events;
